import React from 'react';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import styled from '@emotion/styled';

import useUserContext from '../hooks/use-user-context';
import { createOptionMap } from '../utilities/misc';
import { rem } from '../utilities/style';
import { colors } from '../theme/colors';
import { headers, body } from '../styles/type';
import { media } from '../theme/media';

// There won't be a circular reference because TextRenderer is only used for dynamic text processing
// eslint-disable-next-line import/no-cycle
import TextRenderer from './TextRenderer';
import Button from './buttons/Button';
import { ReactComponent as CalloutPattern } from '../images/icons/callout-pattern.svg';

const Card = styled.div`
  position: relative;
  z-index: 0;
  padding: ${rem(24)};
  border-radius: ${rem(10)};
  background-color: ${colors.blue['500']};
  width: 100%;
  text-align: center;
  margin: 15px 0;

  ${({ layout }) => layout === 'large' && `
    text-align: left;

    ${media('>small')} {
      padding: ${rem(32)};
    }
  `}
`;

const Content = styled.div`

  ${({ layout }) => layout === 'large' && `
    max-width: ${rem(500)};
  `}
`;

const StyledCalloutPattern = styled(CalloutPattern)`
  position: absolute;
  z-index: -1;
  top: 0;
  right: 0;
  display: block;
  height: 100%;
  width: auto;
`;

const Title = styled.div`
  ${headers.h4}
  color: ${colors.white};
  margin-bottom: ${rem(8)};
`;

const Description = styled.div`
  ${body.regular}
  margin-bottom: ${rem(24)};
  color: ${colors.white};
`;

const CalloutCard = ({
  className,
  title,
  description,
  ctaText,
  to,
  targetBlank,
  layout,
}) => {
  const data = useStaticQuery(graphql`
    query CalloutBannerOptions {
      allContentfulOptionSet(filter: {name: {eq: "callout_banner"}}) {
        nodes {
          ...OptionSetFragment
        }
      }
    }
  `);
  const { language } = useUserContext();
  const options = createOptionMap(data.allContentfulOptionSet.nodes, language);
  return (
    <Card className={className} layout={layout}>
      <StyledCalloutPattern />
      <Content layout={layout}>
        <Title>
          <TextRenderer text={title || options['callout_banner.text']} />
        </Title>
        {(description || options['callout_banner.description']) && (
          <Description>
            <TextRenderer text={description || options['callout_banner.description']} />
          </Description>
        )}
        <Button to={to || options['callout_banner.button_link']} light target={targetBlank ? '_blank' : '_self'}>
          <TextRenderer text={ctaText || options['callout_banner.button_text']} />
        </Button>
      </Content>
    </Card>
  );
};

CalloutCard.propTypes = {
  to: PropTypes.string,
  className: PropTypes.string,
  layout: PropTypes.string,
};

export default CalloutCard;
